/**
 * Shared dialog shell used by confirm, info, OTP, and details modals.
 */

import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { AlertCircle, CheckCircle2, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Dialog width token. */
type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
/** Built-in header icon/color pair. */
type HeaderPreset = 'danger' | 'validation';

/** Icon + colors for the danger and validation header presets. */
const HEADER_PRESETS: Record<
  HeaderPreset,
  { icon: LucideIcon; iconColor: string; iconBgColor: string }
> = {
  danger: {
    icon: AlertCircle,
    iconColor: 'rgb(var(--color-error))',
    iconBgColor: 'rgb(var(--color-error) / 0.1)',
  },
  validation: {
    icon: CheckCircle2,
    iconColor: 'rgb(var(--color-success))',
    iconBgColor: 'rgb(var(--color-success) / 0.1)',
  },
};

/** Optional header icon / title / description block. */
interface HeaderConfig {
  preset?: HeaderPreset;
  icon?: LucideIcon;
  iconSize?: 'sm' | 'md' | 'lg';
  iconColor?: string;
  iconBgColor?: string;
  badge?: React.ReactNode;
  title?: string;
  titleSize?: 'sm' | 'md' | 'lg' | 'xl';
  titleColor?: string;
  titleWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
  subtitle?: string;
  subtitleColor?: string;
  description?: string;
}

/** Props for {@link BaseModal}. */
interface BaseModalProps {
  open: boolean;
  onClose: () => void;
  size?: ModalSize;
  className?: string;
  hideCloseButton?: boolean;
  closeOnOutsideClick?: boolean;
  closeOnEscape?: boolean;
  header?: HeaderConfig;
  children: React.ReactNode;
  description?: string;
  footer?: React.ReactNode;
  /** Calendar event type (`session` | `postit` | `holiday`), applied as data-event-type. */
  dataEventType?: string;

  /** Use compact padding for details views to maximize content space */
  compactPadding?: boolean;

  /** Hide the colored content wrapper (for modals that display content via description only) */
  hideContentWrapper?: boolean;

  /** Optional classes on the header / footer separator. */
  headerSeparatorClassName?: string;
  footerSeparatorClassName?: string;

  /**
   * Let dropdowns overflow the modal instead of being clipped by the scroll container.
   * Disables inner scroll — use only on short modals that fit the viewport.
   */
  contentOverflowVisible?: boolean;

  /** When false, the orange card hugs its children instead of filling the body. */
  fillContent?: boolean;
}

/** Dialog max-width per {@link ModalSize}. `3xl` fills the viewport so the body card can match. */
const sizeClasses: Record<ModalSize, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-6xl',
  '3xl':
    'w-[96vw] min-w-[96vw] max-w-[96vw] h-[calc(100dvh-0.5rem)] min-h-[calc(100dvh-0.5rem)] max-h-[calc(100dvh-0.5rem)] gap-2 p-4',
};

/** Header icon pixel size. */
const iconSizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

/** Header icon container pixel size. */
const iconContainerSizeClasses = {
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-12 h-12',
};

/** Header title font size. */
const titleSizeClasses = {
  sm: 'text-base',
  md: 'text-lg',
  lg: 'text-xl',
  xl: 'text-2xl',
};

/** Header title font weight. */
const titleWeightClasses = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

/** Classes for the Radix dialog shell (size, overflow, caller extras). */
function dialogContentClassName(
  size: ModalSize,
  contentOverflowVisible: boolean,
  className?: string,
): string {
  return cn(
    sizeClasses[size],
    size === '3xl' ? undefined : 'max-h-[calc(100dvh-2rem)]',
    contentOverflowVisible ? 'overflow-visible' : 'overflow-hidden',
    className,
  );
}

/** Orange framed body, or raw children when the wrapper is hidden. */
function renderModalBody(input: {
  hideContentWrapper: boolean;
  fillContent: boolean;
  compactPadding: boolean;
  hasHeader: boolean;
  children: React.ReactNode;
}): React.ReactNode {
  if (input.hideContentWrapper) {
    return input.children;
  }
  return (
    <div
      className={cn(
        'relative rounded-2xl border-2 transition-all',
        input.fillContent ? 'flex min-h-0 flex-1 flex-col overflow-y-auto' : 'h-fit shrink-0',
        !input.hasHeader && 'mt-6',
      )}
      style={{
        borderColor: 'var(--color-1-nuance-3)',
        background: 'color-mix(in srgb, var(--color-1-nuance-3) 3%, transparent)',
      }}
    >
      <div
        className={cn(
          input.compactPadding ? 'p-2' : 'p-4',
          input.fillContent && 'flex min-h-full flex-1 flex-col',
        )}
      >
        {input.children}
      </div>
    </div>
  );
}

/** Shared modal shell used by confirm, delete, and info dialogs. */
export const BaseModal: React.FC<BaseModalProps> = ({
  open,
  onClose,
  size = 'md',
  className,
  hideCloseButton = false,
  closeOnOutsideClick = true,
  closeOnEscape = true,
  header,
  children,
  description,
  footer,
  dataEventType,
  compactPadding = false,
  hideContentWrapper = false,
  headerSeparatorClassName,
  footerSeparatorClassName,
  contentOverflowVisible = false,
  fillContent = true,
}) => {
  /** Radix only notifies close (`false`); ignore open-from-dialog. */
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      onClose();
    }
  };

  const headerPreset = header?.preset ? HEADER_PRESETS[header.preset] : undefined;
  const Icon = header?.icon ?? headerPreset?.icon;
  const iconSize = header?.iconSize || 'md';
  const titleSize = header?.titleSize || 'lg';
  const titleWeight = header?.titleWeight || 'bold';
  const iconColor = header?.iconColor ?? headerPreset?.iconColor ?? 'var(--color-2-nuance-1)';
  const iconBgColor =
    header?.iconBgColor ??
    headerPreset?.iconBgColor ??
    'color-mix(in srgb, var(--color-2-nuance-1) 15%, transparent)';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={dialogContentClassName(size, contentOverflowVisible, className)}
        hideCloseButton={hideCloseButton}
        {...(dataEventType ? { 'data-event-type': dataEventType } : {})}
        onPointerDownOutside={(e) => {
          if (!closeOnOutsideClick) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (!closeOnEscape) {
            e.preventDefault();
          }
        }}
      >
        {header && (Icon || header.badge || header.title || header.subtitle) && (
          <DialogHeader className="shrink-0">
            <div className="flex items-center gap-3 pr-8">
              {Icon && (
                <div
                  className={cn(
                    'flex items-center justify-center rounded-xl shrink-0 transition-all',
                    iconContainerSizeClasses[iconSize],
                  )}
                  style={{ backgroundColor: iconBgColor }}
                >
                  <Icon className={iconSizeClasses[iconSize]} style={{ color: iconColor }} />
                </div>
              )}

              {header.title && (
                <DialogTitle
                  className={cn(
                    titleSizeClasses[titleSize],
                    titleWeightClasses[titleWeight],
                    'flex-1',
                  )}
                  style={{ color: header.titleColor || 'var(--foreground)' }}
                >
                  {header.title}
                </DialogTitle>
              )}

              {header.badge && <div className="shrink-0">{header.badge}</div>}
            </div>

            {header.subtitle && (
              <p
                className="text-sm mt-1"
                style={{ color: header.subtitleColor || 'var(--muted-foreground)' }}
              >
                {header.subtitle}
              </p>
            )}

            {header.description && (
              <p className={cn('text-xs text-muted-foreground mt-1.5', Icon && 'pl-[3.25rem]')}>
                {header.description}
              </p>
            )}

            {description && (
              <DialogDescription className="mt-3 whitespace-pre-line">
                {description}
              </DialogDescription>
            )}

            {(Icon || header.title) && (
              <div
                className={cn(
                  'h-px bg-gradient-to-r from-transparent via-[color-mix(in_srgb,var(--color-2-nuance-1)_20%,transparent)] to-transparent mt-4',
                  headerSeparatorClassName,
                )}
              />
            )}
          </DialogHeader>
        )}

        {/* Hidden DialogTitle for accessibility when header.title is missing */}
        {!header?.title && (
          <VisuallyHidden>
            <DialogTitle>Modal</DialogTitle>
          </VisuallyHidden>
        )}

        {/* Scrollable body: keeps the scrollbar inside the modal padding, away from
            the rounded corners, while header/footer stay fixed */}
        <div
          className={cn(
            'base-modal-scroll relative flex min-h-0 flex-1 flex-col',
            contentOverflowVisible ? 'overflow-visible' : 'overflow-hidden',
          )}
        >
          {renderModalBody({
            hideContentWrapper,
            fillContent,
            compactPadding,
            hasHeader: Boolean(header),
            children,
          })}
        </div>

        {footer && (
          <div className="shrink-0">
            <div
              className={cn(
                'h-px bg-gradient-to-r from-transparent via-[color-mix(in_srgb,var(--color-1-nuance-3)_30%,transparent)] to-transparent mb-4',
                footerSeparatorClassName,
              )}
            />
            <DialogFooter>{footer}</DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
